import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { HomeWeather } from "@/components/HomeWeather";
import { TourPlayer } from "@/components/TourPlayer";
import { useStreak } from "@/context/StreakContext";
import { ShareStreak } from "@/components/ShareStreak";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Compass, Bot, MessageSquareQuote, BookOpen, Wand2, History, Lightbulb, Feather,
  ArrowRight, ShieldCheck, Languages, Tag, Sparkles, Flame, Trophy, Share2, GripVertical,
} from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const STORY = "/tiles/bg/storytelling.jpg";

const TILES = [
  { id: "explore", icon: Compass, persona: "explore", span: "md:col-span-8 lg:col-span-8 md:row-span-2", hero: true,
    bg: "/tiles/bg/explore.jpg",
    blurb: "Discover topics, cultures and places with a curious guide." },
  { id: "assistants", icon: Bot, persona: "assistant", span: "md:col-span-4 lg:col-span-4",
    bg: "/tiles/bg/assistants.jpg",
    blurb: "Task-focused helpers for drafting, planning and research." },
  { id: "prompts", icon: MessageSquareQuote, persona: "general", span: "md:col-span-4 lg:col-span-4 md:row-span-2",
    bg: "/tiles/bg/prompts.jpg",
    blurb: "Community favourites, ready in your language." },
  { id: "instructions", icon: BookOpen, persona: "tutor", span: "md:col-span-4 lg:col-span-4",
    bg: "/tiles/bg/instructions.jpg",
    blurb: "Step-by-step guidance for anything you're learning." },
  { id: "elite", icon: Wand2, persona: "elite", span: "md:col-span-4 lg:col-span-4",
    bg: "/tiles/bg/elite.jpg",
    blurb: "Advanced code, data and strategy tooling." },
  { id: "history", icon: History, persona: "general", span: "md:col-span-4 lg:col-span-4",
    bg: "/tiles/bg/history.jpg",
    blurb: "Every conversation, saved and searchable." },
  { id: "suggestions", icon: Lightbulb, persona: "general", span: "md:col-span-6 lg:col-span-6",
    bg: "/tiles/bg/suggestions.jpg",
    blurb: "Personalised ideas based on how you chat." },
  { id: "storytelling", icon: Feather, persona: "storyteller", span: "md:col-span-6 lg:col-span-6",
    bg: "/tiles/bg/storytelling.jpg",
    blurb: "Vivid short stories written just for you." },
];

const PROMPT_META = Object.fromEntries(
  [
    ["explore:music", "Music Generation", "/tiles/music.jpg"],
    ["explore:image", "AI Image Generator", "/tiles/image.jpg"],
    ["explore:summarizers", "AI Summarizers", "/tiles/summarize.jpg"],
    ["elite:logo", "AI Logo Maker", "/tiles/logo.jpg"],
    ["elite:tattoo", "AI Tattoo", "/tiles/tattoo.jpg"],
    ["elite:detector", "AI Detector", "/tiles/detector.jpg"],
    ["elite:creations", "My Creations", "/tiles/creations.jpg"],
    ["elite:image", "AI Image Generator", "/tiles/image.jpg"],
    ["tutor:homework_help", "Get homework help", "/tiles/homework.jpg"],
    ["tutor:test_prep", "Prepare for tests", "/tiles/testprep.jpg"],
    ["tutor:explore_subjects", "Explore new subjects", "/tiles/explore.jpg"],
    ["assistant:web_owl", "Web Owl", "/avatars/web_owl.jpg"],
    ["assistant:dating_advisor", "Dating Advisor", "/avatars/dating_advisor.jpg"],
    ["assistant:financial_assistant", "Financial Assistant", "/avatars/financial_assistant.jpg"],
    ["assistant:wellness_advisor", "Wellness Advisor", "/avatars/wellness_advisor.jpg"],
    ["assistant:study_helper", "Study Helper", "/avatars/study_helper.jpg"],
    ["storyteller:story_everyone", "Stories for Everyone", "/avatars/story_everyone.jpg"],
    ["storyteller:story_kids", "Stories for Kids", "/avatars/story_kids.jpg"],
    ["storyteller:story_adults", "Stories for Adults", "/avatars/story_adults.jpg"],
    ["storyteller:story_comic", "Comic Books", "/avatars/story_comic.jpg"],
    ["storyteller:story_descriptive", "Descriptive Stories", "/avatars/story_descriptive.jpg"],
    ["storyteller:story_location", "Location Descriptions", "/avatars/story_location.jpg"],
  ].map(([key, label, image]) => [key, { key, label, image }])
);

const TOOL_PATHS = {
  "explore:music": "/tools/music",
  "explore:image": "/tools/image",
  "explore:summarizers": "/tools/summarize",
  "elite:image": "/tools/image",
  "elite:logo": "/tools/logo",
  "elite:tattoo": "/tools/tattoo",
  "elite:detector": "/tools/detector",
  "elite:creations": "/creations",
};

const promptTarget = (key) => TOOL_PATHS[key] || `/chat?prompt=${key}`;

export default function Landing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [promos, setPromos] = useState([]);
  const [peek, setPeek] = useState(null);
  const [streak, setStreak] = useState(null);
  const { streak: run, board } = useStreak();
  const [sharing, setSharing] = useState(false);
  const [order, setOrder] = useState([]);
  const [dragTile, setDragTile] = useState(null);

  useEffect(() => {
    api.get("/promotions/active").then(({ data }) => setPromos(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return setPeek(null);
    let alive = true;
    api.get("/home/peek").then(({ data }) => {
      if (!alive) return;
      setPeek(data);
      setOrder(data.tile_order || []);
    }).catch(() => {});
    api.get("/home/streak").then(({ data }) => alive && setStreak(data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [user]);

  const streakLine = useMemo(() => {
    if (run?.current > 0) {
      const d = run.current === 1 ? "day" : "days";
      if (run.next)
        return `${run.current} ${d} in a row — ${run.next.remaining} more for ${run.next.label}.`;
      return `${run.current} ${d} in a row. Every streak reward is yours.`;
    }
    if (!streak) return null;
    const { stories_week: s, chats_week: c, active_days: d } = streak;
    if (s > 0 && d > 1) return `${s} ${s === 1 ? "story" : "stories"} and ${d} days of chatting this week — keep it going.`;
    if (s > 0) return `${s} ${s === 1 ? "story" : "stories"} this week. Ready for the next one?`;
    if (c > 0) return `${c} ${c === 1 ? "chat" : "chats"} this week — try turning one into a story.`;
    return "Nothing yet this week. A story takes about two minutes.";
  }, [streak, run]);

  const suggestion = useMemo(() => {
    const used = new Set((peek?.top_prompts || []).map((p) => p.prompt_key));
    const fresh = Object.values(PROMPT_META).filter((x) => !used.has(x.key));
    return fresh[Math.floor(Math.random() * fresh.length)] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peek?.top_prompts?.length, peek?.top_prompts?.[0]?.prompt_key]);

  const promptMeta = (key) => {
    const m = PROMPT_META[key];
    if (!m) return null;
    const custom = peek?.prompt_labels?.[key];
    return custom ? { ...m, label: custom } : m;
  };

  const peekFor = (tile) => {
    if (!peek) return null;
    const topIn = (prefix) => {
      const hit = (peek.top_prompts || []).find((p) => p.prompt_key.startsWith(prefix));
      return hit ? promptMeta(hit.prompt_key) : null;
    };
    if (tile.id === "storytelling" && peek.latest_story) {
      return { label: peek.latest_story.title, hint: "Latest story",
               creationId: peek.latest_story.cover_creation_id, to: `/stories/${peek.latest_story.story_id}` };
    }
    if (tile.id === "elite" && peek.latest_creation) {
      return { label: peek.latest_creation.title || peek.latest_creation.prompt?.slice(0, 40),
               hint: `Latest ${peek.latest_creation.kind}`,
               creationId: peek.latest_creation.creation_id, to: "/creations" };
    }
    if (tile.id === "history" && peek.latest_session) {
      return { label: peek.latest_session.title, hint: `${peek.chats} chats · continue`,
               to: `/chat?session=${peek.latest_session.session_id}` };
    }
    if (tile.id === "prompts") {
      const m = (peek.top_prompts || []).map((p) => promptMeta(p.prompt_key)).find(Boolean);
      return m && { label: m.label, hint: "Your most used", image: m.image, to: promptTarget(m.key) };
    }
    if (tile.id === "assistants") {
      const m = topIn("assistant:");
      return m && { label: m.label, hint: "Most used assistant", image: m.image, to: promptTarget(m.key) };
    }
    if (tile.id === "instructions") {
      const m = topIn("tutor:");
      return m && { label: m.label, hint: "Most used tutor", image: m.image, to: promptTarget(m.key) };
    }
    if (tile.id === "suggestions") {
      const m = suggestion;
      return m && { label: m.label, hint: "Try something new", image: m.image, to: promptTarget(m.key) };
    }
    if (tile.id === "explore") {
      const m = topIn("explore:") || topIn("storyteller:");
      return m && { label: m.label, hint: "Pick up where you left off", image: m.image, to: promptTarget(m.key) };
    }
    return null;
  };

  const go = (tile) => nav(user ? `/chat?persona=${tile.persona}&tile=${tile.id}` : "/signup");

  // The member's own tile order, saved so home always opens on what they use most.
  const tiles = useMemo(() => {
    if (!order.length) return TILES;
    const rank = (id) => (order.indexOf(id) === -1 ? 99 : order.indexOf(id));
    return [...TILES].sort((a, b) => rank(a.id) - rank(b.id));
  }, [order]);

  const dropTile = async (target) => {
    const dragged = dragTile;
    setDragTile(null);
    if (!dragged || dragged === target.id) return;
    const ids = tiles.map((x) => x.id).filter((id) => id !== dragged);
    ids.splice(ids.indexOf(target.id), 0, dragged);
    setOrder(ids);
    try {
      await api.put("/home/tiles", { ids });
    } catch (e) {
      console.error("could not save the tile order", e);
      toast.error("Could not save that order");
    }
  };

  return (
    <div data-testid="landing-page">
      <section className="grain relative overflow-hidden border-b border-white/10">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-12 sm:px-8 sm:pb-28 sm:pt-[4.5rem]">
          <div className="max-w-3xl rise">
            <h1 data-testid="brand-title" className="sr-only">
              ChatMiLu
            </h1>
            <img
              src="/logo.png"
              alt="ChatMiLu"
              data-testid="brand-logo-hero"
              className="w-auto drop-shadow-[0_0_36px_rgba(26,125,255,0.28)]"
              style={{ height: "clamp(5rem, 15vw, 9.5rem)" }}
            />
            <p
              data-testid="brand-slogan"
              dir="auto"
              className="mt-3 max-w-xl font-display text-base font-medium text-ink-soft md:text-lg"
            >
              {t("slogan")}
            </p>

            <p className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0B0C0F]/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-forest">
              <Languages className="h-3.5 w-3.5" /> {t("heroKicker")}
            </p>
            <h2
              dir="auto"
              className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
            >
              {t("heroTitle")}
            </h2>
            <p dir="auto" className="mt-5 max-w-2xl text-sm leading-relaxed text-ink-soft md:text-base">
              {t("heroBody")}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="hero-start-chatting"
                onClick={() => nav(user ? "/chat" : "/signup")}
                className="min-h-[48px] rounded-full bg-brand px-7 text-base hover:bg-brand-hover active:scale-95"
              >
                {t("startChatting")} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
              </Button>
              <Button
                data-testid="hero-see-plans"
                variant="outline"
                onClick={() => nav("/pricing")}
                className="min-h-[48px] rounded-full border-white/20 bg-panel px-7 text-base active:scale-95"
              >
                {t("seePlans")}
              </Button>
              {/* where MiLu stands on the home page: just right of the two buttons */}
              <span id="milu-anchor" aria-hidden="true" className="hidden h-1 w-1 lg:block" />
            </div>
          </div>

          <div className="mt-12 max-w-3xl rise" data-testid="hero-tour">
            <TourPlayer />
            <Link
              to="/tour"
              data-testid="hero-tour-link"
              className="mt-4 inline-flex min-h-[42px] items-center gap-2 rounded-full border border-brand/50 bg-brand/10 px-5 text-sm font-semibold text-brand transition-colors hover:border-brand hover:bg-brand/20 active:scale-95"
            >
              <Share2 className="h-4 w-4" /> Open the tour on its own page — easy to share
            </Link>
          </div>
        </div>
      </section>

      {promos.length > 0 && (
        <section className="border-b border-white/10 bg-forest/10" data-testid="promo-strip">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-8 gap-y-2 px-5 py-3 text-sm sm:px-8">
            {promos.map((p) => (
              <span key={p.promo_id} data-testid={`promo-${p.code}`} className="inline-flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-forest" />
                <b className="font-semibold">{p.code}</b>
                <span className="text-ink-soft">{p.description}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-5 pb-20 pt-16 sm:px-8">
        <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Start Anywhere...</h2>
        {streakLine && (
          <button
            data-testid="home-streak"
            onClick={() => nav("/chat?persona=storyteller")}
            className="mt-4 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-panel px-5 py-2.5 text-sm text-ink-soft transition-colors hover:border-brand/40 hover:text-ink"
          >
            <Flame className="h-4 w-4 text-brand" />
            <span dir="auto">{streakLine}</span>
            <ArrowRight className="h-3.5 w-3.5 text-brand rtl:rotate-180" />
          </button>
        )}
        <p className="mt-2 max-w-xl text-sm text-ink-soft md:text-base">
          Eight ways into ChatMiLu — each one speaks your language.
          {user && <span data-testid="tile-drag-hint"> Drag a tile to move your favourites to the top.</span>}
        </p>

        {user && <HomeWeather />}

        {board?.top?.length > 0 && (          <div
            data-testid="streak-leaderboard"
            className="mt-8 max-w-xl rounded-3xl border border-white/10 bg-panel p-6"
          >
            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
                <Trophy className="h-4 w-4 text-amber-300" /> Longest streaks
              </p>
              <span className="text-xs text-ink-soft">{board.month}</span>
            </div>
            <ol className="mt-4 space-y-2">
              {board.top.map((p) => (
                <li
                  key={p.rank}
                  data-testid={`leader-${p.rank}`}
                  className={`flex items-center gap-3 rounded-2xl px-3 py-2 text-sm ${
                    p.is_me ? "bg-brand/10 font-semibold text-brand" : "text-ink-soft"
                  }`}
                >
                  <span
                    className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${
                      p.rank === 1
                        ? "bg-amber-300 text-black"
                        : p.rank === 2
                        ? "bg-slate-300 text-black"
                        : p.rank === 3
                        ? "bg-amber-600 text-black"
                        : "border border-white/10 text-ink-soft"
                    }`}
                  >
                    {p.rank}
                  </span>
                  <span className="flex-1 truncate" dir="auto">{p.is_me ? "You" : p.name}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-ink">
                    <Flame className="h-3.5 w-3.5 text-orange-400" />
                    {p.current} {p.current === 1 ? "day" : "days"}
                  </span>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-xs text-ink-soft">
              {board.me
                ? `You are #${board.me.rank} of ${board.players} — one message a day keeps you climbing.`
                : "Send a message today to get on the board."}
            </p>
            {run?.current > 0 && (
              <button
                data-testid="share-streak-open-home"
                onClick={() => setSharing(true)}
                className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
              >
                <Share2 className="h-4 w-4" /> Share my streak
              </button>
            )}
            <ShareStreak open={sharing} onOpenChange={setSharing} />
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          {tiles.map((tile, i) => {
            const Icon = tile.icon;
            const p = peekFor(tile);
            return (
              <button
                key={tile.id}
                data-testid={`tile-${tile.id}`}
                onClick={() => go(tile)}
                draggable={Boolean(user)}
                onDragStart={() => setDragTile(tile.id)}
                onDragOver={(e) => dragTile && e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  dropTile(tile);
                }}
                onDragEnd={() => setDragTile(null)}
                style={{ animationDelay: `${i * 60}ms` }}
                className={`rise group relative flex min-h-[150px] flex-col justify-between overflow-hidden rounded-3xl border bg-panel p-7 text-start shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${
                  dragTile === tile.id ? "border-brand/60 opacity-60" : "border-white/10"
                }`}
              >
                {tile.bg && (
                  <>
                    <img
                      src={tile.bg}
                      alt=""
                      loading="lazy"
                      data-testid={`tile-bg-${tile.id}`}
                      className="pointer-events-none absolute inset-0 h-full w-full scale-105 object-cover opacity-70 brightness-100 transition-all duration-500 group-hover:scale-110 group-hover:opacity-100 group-hover:brightness-[1.75] group-hover:saturate-150"
                    />
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#07080A] via-[#07080A]/55 to-transparent transition-opacity duration-500 group-hover:opacity-40" />
                  </>
                )}
                <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0B0C0F]/70 text-forest backdrop-blur-md transition-colors group-hover:bg-brand group-hover:text-white">
                  <Icon className="h-5 w-5" />
                </span>
                {user && (
                  <GripVertical
                    data-testid={`tile-grip-${tile.id}`}
                    className="pointer-events-none absolute end-4 top-4 h-4 w-4 text-ink-soft opacity-0 transition-opacity group-hover:opacity-100"
                  />
                )}
                <span className="relative mt-6 block">
                  <span dir="auto" className="block font-display text-lg font-bold tracking-tight drop-shadow">
                    {t(`tiles.${tile.id}`)}
                  </span>
                  <span dir="auto" className="mt-1 block text-sm text-ink-soft">
                    {tile.blurb}
                  </span>
                  {p && (
                    <span
                      data-testid={`tile-peek-${tile.id}`}
                      role="link"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        nav(p.to);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          nav(p.to);
                        }
                      }}
                      className="mt-4 flex items-center gap-2.5 rounded-2xl border border-white/10 bg-[#0B0C0F]/80 p-2 backdrop-blur-md transition-colors hover:border-brand/40"
                    >
                      <span className="h-9 w-9 shrink-0 overflow-hidden rounded-xl bg-surfacealt">
                        {p.creationId ? (
                          <AuthImage creationId={p.creationId} alt="" className="h-full w-full object-cover" />
                        ) : p.image ? (
                          <img src={p.image} alt="" width="36" height="36" className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full items-center justify-center">
                            <Sparkles className="h-4 w-4 text-brand" />
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[11px] uppercase tracking-widest text-ink-soft">{p.hint}</span>
                        <span dir="auto" className="block truncate text-xs font-semibold">{p.label}</span>
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-brand rtl:rotate-180" />
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/10 bg-panel">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand">Messages</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              Everyone writes in their own language.
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-ink-soft md:text-base">
              Create a room, share the invite code, and ChatMiLu translates every message into each member's
              preferred language the moment it lands. No copy-paste. No misunderstandings.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Per-member language preference across 50 languages",
                "Group rooms with mixed-language participants",
                "AI language-learning tips drawn from your real chats",
                "Translation cache for instant repeat delivery",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
                  <span className="text-ink-soft">{f}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid="transmessages-cta"
              onClick={() => nav(user ? "/messages" : "/signup")}
              className="mt-9 min-h-[48px] rounded-full bg-forest px-7 hover:bg-forest/90 active:scale-95"
            >
              Open Messages <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
            </Button>
          </div>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-page p-7">
            <div className="space-y-4">
              {[
                { n: "Mai", l: "Tiếng Việt", o: "Chào cả nhà, mình vừa đến Paris!", tr: "Hi everyone, I just arrived in Paris!" },
                { n: "Amir", l: "العربية", o: "مرحبًا مي! كيف الجو هناك؟", tr: "Hello Mai! How is the weather there?" },
                { n: "Lena", l: "Deutsch", o: "Viel Spaß, schick Fotos!", tr: "Have fun, send photos!" },
              ].map((m, i) => (
                <div
                  key={m.n}
                  style={{ animationDelay: `${i * 120}ms` }}
                  className="rise rounded-3xl rounded-bl-sm border border-white/10 bg-panel p-4 shadow-sm"
                >
                  <p className="text-xs font-semibold text-ink-soft">
                    {m.n} · {m.l}
                  </p>
                  <p dir="auto" className="mt-1.5 text-sm font-medium">{m.o}</p>
                  <p dir="auto" className="mt-1 border-t border-dashed border-white/15 pt-1.5 text-sm text-forest">
                    {m.tr}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <img src="/logo.png" alt="ChatMiLu" data-testid="brand-logo-footer" className="h-10 w-auto" />
            <p className="mt-1 text-sm text-ink-soft">{t("slogan")}</p>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-ink-soft">
            <Link to="/pricing" data-testid="footer-pricing">Plans</Link>
            <Link to="/support" data-testid="footer-support">Support us</Link>
            <Link to="/tour" data-testid="footer-tour">Watch the tour</Link>
            <Link to="/press" data-testid="footer-press">Press kit</Link>
            <Link to="/hall-of-fame" data-testid="footer-hall">Hall of fame</Link>
            <Link to="/settings" data-testid="footer-settings">Settings & privacy</Link>
            <span>GDPR-ready · {lang.toUpperCase()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
